import { useState } from "react";
import { MapPin, Pencil, Trash2, Check } from "lucide-react";
import DeliveryLocationPicker from "../DeliveryLocationPicker/DeliveryLocationPicker";
import Button from "../Button/Button";
import "./CustomerAddressField.scss";

/**
 * Zoznam adries, ktore si zadava ZAKAZNIK (R23).
 *
 * PRECO SA ADRESA NEPISE VOLNYM TEXTOM:
 *   Dispecer pozna svoj rozvoz a adresu si napise, ako mu vyhovuje. Zakaznik
 *   ale zadava miesto, kam sa ma niekto dostat autom, a nema ako vediet, co
 *   vodicovi staci. Preto sa adresa VYBERA z ponuky (Google Places) a poloha
 *   sa potvrdzuje spendlikom na mape -- rovnakou obrazovkou, aku uz pouziva
 *   obsluha, takze sa neudrziavaju dve mapy.
 *
 *   Bez potvrdeneho bodu sa adresa neda ulozit; server to kontroluje znova
 *   (services/customerAddresses.js) a bez suradnic vrati 400.
 *
 * Spresnenie ("3. poschodie, zvoncek Novak") je uz volny text -- do navigacie
 * nevstupuje, cita ho clovek.
 *
 * Props:
 *  - adresy     {array}  [{ address, address_note, latitude, longitude }]
 *  - onChange   {fn}     (adresy) => void
 *  - max        {number} kolko adries sa da pridat
 *  - disabled   {bool}
 */
const MAX_POZNAMKA = 100;

function CustomerAddressField({ adresy = [], onChange, max = 10, disabled = false }) {
  // null = mapa je zavreta, cislo = index upravovanej adresy,
  // "nova" = pridava sa dalsia
  const [mapaPre, setMapaPre] = useState(null);

  const upravovana = typeof mapaPre === "number" ? adresy[mapaPre] : null;

  const potvrdPolohu = (poloha, text) => {
    if (!poloha) return;
    const zaznam = {
      address: text || upravovana?.address || "",
      address_note: upravovana?.address_note || "",
      latitude: poloha.lat,
      longitude: poloha.lng,
    };

    if (typeof mapaPre === "number") {
      onChange(adresy.map((a, i) => (i === mapaPre ? { ...a, ...zaznam } : a)));
    } else {
      onChange([...adresy, zaznam]);
    }
    setMapaPre(null);
  };

  const zmenPoznamku = (index, hodnota) =>
    onChange(adresy.map((a, i) => (i === index ? { ...a, address_note: hodnota.slice(0, MAX_POZNAMKA) } : a)));

  const odstran = (index) => onChange(adresy.filter((_, i) => i !== index));

  return (
    <div className="customer-address">
      {adresy.length === 0 && (
        <p className="customer-address__empty">
          Zatiaľ nemáte pridanú žiadnu adresu. Vyhľadajte ju a potvrďte polohu na mape.
        </p>
      )}

      <ul className="customer-address__list">
        {adresy.map((a, index) => (
          <li key={`${a.address}-${index}`} className="customer-address__item">
            <div className="customer-address__head">
              <MapPin size={18} aria-hidden="true" />
              <span className="customer-address__text">{a.address}</span>
              <span className="customer-address__ok" title="Poloha je potvrdená">
                <Check size={16} aria-hidden="true" />
              </span>
            </div>

            <label className="customer-address__note">
              <span>Spresnenie pre vodiča (nepovinné)</span>
              <input
                type="text"
                value={a.address_note || ""}
                onChange={(e) => zmenPoznamku(index, e.target.value)}
                placeholder="3. poschodie, zvonček Novák"
                maxLength={MAX_POZNAMKA}
                disabled={disabled}
              />
            </label>

            <div className="customer-address__actions">
              <button
                type="button"
                className="customer-address__link"
                onClick={() => setMapaPre(index)}
                disabled={disabled}
              >
                <Pencil size={15} aria-hidden="true" /> Upraviť adresu
              </button>
              <button
                type="button"
                className="customer-address__link customer-address__link--danger"
                onClick={() => odstran(index)}
                disabled={disabled}
              >
                <Trash2 size={15} aria-hidden="true" /> Odstrániť
              </button>
            </div>
          </li>
        ))}
      </ul>

      {adresy.length < max && (
        <Button
          type="button"
          variant="outline"
          onClick={() => setMapaPre("nova")}
          disabled={disabled}
        >
          <MapPin size={17} aria-hidden="true" />
          {adresy.length === 0 ? "Pridať adresu" : "Pridať ďalšiu adresu"}
        </Button>
      )}

      {adresy.length >= max && (
        <p className="customer-address__hint">Na jeden účet sa dá uložiť najviac {max} adries.</p>
      )}

      <DeliveryLocationPicker
        isOpen={mapaPre !== null}
        address={upravovana?.address || ""}
        location={
          upravovana && Number.isFinite(Number(upravovana.latitude))
            ? { lat: Number(upravovana.latitude), lng: Number(upravovana.longitude) }
            : null
        }
        onConfirm={potvrdPolohu}
        onClose={() => setMapaPre(null)}
      />
    </div>
  );
}

export default CustomerAddressField;
